/**
 * usePinnedOptions — MultiSelect's "selected rows first" ordering, plus the cap
 * on how many rows render at once.
 * For Media Devoted | Created By James Alexandrov
 *
 * Pinning is a SNAPSHOT, never a live sort: recomputing it on every toggle
 * relocated the row you had just ticked to index 0 and threw the scroll back to
 * the top of the list. The snapshot is re-taken when the option set or the
 * search changes — and when the selection is set from OUTSIDE the component.
 *
 * That last case is MD-1020: the Duplicate dialogs pre-select a flow once
 * react-query answers, long after the list first rendered, so a snapshot taken
 * at mount pinned nothing and the ticked row sat wherever it fell
 * alphabetically. "Outside" means "not an echo of what this component last
 * emitted" — a plain did-I-change-it flag would stay stuck the moment a parent
 * dropped an onChange, and swallow the next real seed.
 */

import { useMemo, useRef } from "react";

interface Pinnable {
  value: string;
}

interface Params<T extends Pinnable> {
  /** The rows left by the search, in their natural order. */
  filtered: T[];
  /** The current selection (the caller owns it). */
  value: string[];
  onChange: (next: string[]) => void;
  /** Re-snapshot when this changes — the option set or the search moved. */
  pinKey: string;
  pinSelected?: boolean;
  maxRender?: number;
}

export interface PinnedOptions<T> {
  /** The rows to render: pinned first, then the rest, capped by maxRender. */
  shown: T[];
  /** Every change the component makes goes through here, so its own echo is
   *  never mistaken for an outside re-seed. */
  emit: (next: string[]) => void;
}

const sameIds = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i]);

export function usePinnedOptions<T extends Pinnable>({
  filtered,
  value,
  onChange,
  pinKey,
  pinSelected,
  maxRender,
}: Params<T>): PinnedOptions<T> {
  const pin = useRef({ key: "", ids: new Set<string>() });
  const emitted = useRef<string[] | null>(null);
  const seen = useRef<string[]>(value);

  const echo = !!emitted.current && sameIds(emitted.current, value);
  const fromOutside = !echo && !sameIds(seen.current, value);
  seen.current = value;
  if (pin.current.key !== pinKey || fromOutside) {
    pin.current = { key: pinKey, ids: new Set(pinSelected ? value : []) };
  }
  const pinned = pin.current.ids;

  const shown = useMemo(() => {
    const first = filtered.filter((o) => pinned.has(o.value));
    const rest = filtered.filter((o) => !pinned.has(o.value));
    // Reorder only when pinning is on — the cap must not pin an unpinned list.
    const ordered = pinSelected && first.length ? [...first, ...rest] : filtered;
    if (!maxRender || ordered.length <= maxRender) return ordered;
    return ordered.slice(0, Math.max(maxRender, first.length));
  }, [filtered, maxRender, pinSelected, pinned]);

  const emit = (next: string[]) => {
    emitted.current = next;
    onChange(next);
  };

  return { shown, emit };
}
